import * as py from "../runner/py-util";
import { s_anvil_events, s_anvil_hooks, s_dom_element, s_page_added, s_page_removed, s_page_shown, s_set_data_binding_listener, s_setup_dom } from "../runner/py-util";
// The *real* base Component class. It implements the event APIs, the _anvil_hooks_/anvil$hooks API, and that's it.
// A subclass can specify a set of events that can be raised on this component by providing a list in _anvil_events
// (Currently this is a list of strings, but that will probably change when components occur)
const unwrapDomElement = (elt, pyComponent) => {
    if (!elt?.js$wrapped?.tagName) {
        throw new Sk.builtin.ValueError(`"${Sk.abstr.typeName(pyComponent)}" component did not provide a valid DOM element (got ${Sk.abstr.typeName(elt)} instead)`);
    }
    return elt.js$wrapped;
};
const mkJsHooksForPython = (pyComponent) => {
    // TODO the retryOptionalSuspensionOrThrow() here should be unnecessary (the gattr() doesn't have canSuspend);
    //  it's working around a Skulpt bug where property descriptors (and, I suspect, descriptors in general)
    //  don't check their canSuspend before calling into Python
    const pyHooks = Sk.misceval.retryOptionalSuspensionOrThrow(Sk.abstr.gattr(pyComponent, s_anvil_hooks));
    const callMethod = (method, args) => Sk.misceval.callsimOrSuspendArray(Sk.abstr.gattr(pyHooks, method), args);
    return {
        // Return an implementation of the JS API (anvil$hooks) that's backed off the Python implementation (_anvil_hooks_)
        setupDom() { return Sk.misceval.chain(callMethod(s_setup_dom, []), elt => unwrapDomElement(elt, pyComponent)); },
        get domElement() { return unwrapDomElement(Sk.abstr.gattr(pyHooks, s_dom_element), pyComponent); },
        pageAdded() { return callMethod(s_page_added, []); },
        pageRemoved() { return callMethod(s_page_removed, []); },
        pageShown() { return callMethod(s_page_shown, []); },
        setDataBindingListener(listenFn) {
            return callMethod(s_set_data_binding_listener, [new Sk.builtin.func((pyComponent, pyAttrName, pyNewValue) => listenFn(pyComponent, pyAttrName.toString(), pyNewValue))]);
        }
    };
};
// An implementation of the new Python API backed off the new JS API
export const PyHooks = Sk.abstr.buildNativeClass("anvil.Component._AnvilHooks", {
    constructor: function AnvilHooks(hooks) {
        this.hooks = hooks;
    },
    slots: {
        tp$init(args, kws) {
            Sk.abstr.checkOneArg("_AnvilHooks", args, kws);
            const [component] = args;
            this.hooks = component.anvil$hooks;
        },
    },
    methods: {
        page_added: {
            $meth() {
                return this.hooks.pageAdded();
            },
            $flags: { NoArgs: true },
        },
        page_removed: {
            $meth() {
                return this.hooks.pageRemoved();
            },
            $flags: { NoArgs: true },
        },
        page_shown: {
            $meth() {
                return this.hooks.pageShown();
            },
            $flags: { NoArgs: true },
        },
        set_data_binding_listener: {
            $meth(listenerFn) {
                return this.hooks.setDataBindingListener((pyComponent, attrName, pyNewValue) => Sk.misceval.callsimOrSuspendArray(listenerFn, [
                    pyComponent,
                    new Sk.builtin.str(attrName),
                    pyNewValue,
                ]));
            },
            $flags: { OneArg: true },
        },
        setup_dom: {
            $meth() {
                return;
            }
        }
    },
    getsets: {
        dom_element: {
            $get() {
                return this.hooks.setupDom();
            },
        },
    },
});
export const ComponentTag = Sk.abstr.buildNativeClass("anvil.ComponentTag", {
    constructor: function ComponentTag() { },
    methods: {
        __serialize__: {
            $meth() { return this.tp$getattr(py.str.$dict); }
        },
        __repr__: {
            $meth() {
                const dict = this.tp$getattr(py.str.$dict);
                return new py.str("ComponentTag(" + Sk.misceval.objectRepr(dict) + ")");
            }
        },
    },
    getsets: {
        __dict__: Sk.generic.getSetDict,
    },
    flags: { sk$klass: true },
});
export const Component = Sk.abstr.buildNativeClass("anvil.Component", {
    constructor: function Component() { },
    slots: {
        tp$new(args, kws) {
            const cls = this.constructor;
            const self = new cls();
            const { _ComponentClass: { allowedEvents, providesHooksInPython } } = cls;
            self._Component = { allowedEvents, eventHandlers: {}, tag: new ComponentTag() };
            if (providesHooksInPython) {
                self.anvil$hooks = mkJsHooksForPython(self);
            }
            //console.log("Class", cls.toString(), "has permitted events", allowedEvents);
            return self;
        }
    },
    classmethods: {
        __init_subclass__: {
            $meth(args) {
                const pyAllowedEvents = this.tp$getattr(s_anvil_events);
                const allowedEvents = new Set((pyAllowedEvents ? Sk.misceval.arrayFromIterable(pyAllowedEvents) : []).map(s => s.toString()));
                const providesHooksInPython = this.$typeLookup(s_anvil_hooks) !== Component.$typeLookup(s_anvil_hooks);
                this._ComponentClass = { allowedEvents, providesHooksInPython };
                return Sk.builtin.none.none$;
            },
            $flags: { FastCall: true, NoKwargs: true },
        },
    },
    proto: {
        $verifyEventName(eventName, msg) {
            const { allowedEvents } = this._Component;
            if (!Sk.builtin.checkString(eventName)) {
                throw new Sk.builtin.TypeError("expected the first argument to be a string");
            }
            eventName = eventName.toString();
            if (eventName.startsWith("x-") || allowedEvents.has(eventName)) {
                return eventName;
            }
            else {
                throw new Sk.builtin.ValueError(`Cannot ${msg} for unknown event '${eventName}' on ${this.tp$name} component. Custom event names must start with 'x-'.`);
            }
        },
        $verifyCallable(eventName, pyHandler) {
            if (!Sk.builtin.checkCallable(pyHandler)) {
                throw new Sk.builtin.TypeError(`The '${eventName}' event handler added to ${this.tp$name} must be a callable, not type '${Sk.abstr.typeName(pyHandler)}'`);
            }
        },
        anvilComponent$setParent(pyParent, remove) {
            this._Component.parent = { pyParent, remove };
        },
    },
    methods: {
        /*!defBuiltinMethod(,event_name, handler_func:callable)!2*/
        "set_event_handler": {
            $meth: function (pyEventName, pyHandler) {
                const { allowedEvents, eventHandlers } = this._Component;
                const eventName = this.$verifyEventName(pyEventName, "set event handler for");
                if (Sk.builtin.checkNone(pyHandler)) {
                    // we delete as a signal that the event handlers for this event don't exist. See link click event for example
                    delete this._Component.eventHandlers[eventName];
                }
                else {
                    // replace existing handlers with the new handler
                    this.$verifyCallable(eventName, pyHandler);
                    this._Component.eventHandlers[eventName] = [pyHandler];
                }
                return Sk.builtin.none.none$;
            },
            $flags: { MinArgs: 2, MaxArgs: 2 },
            $doc: "Set a function to call when the 'event_name' event happens on this component. Using set_event_handler removes all other handlers. Setting the handler function to None removes all handlers.",
        },
        /*!defBuiltinMethod(,event_name, handler_func:callable)!2*/
        "add_event_handler": {
            $meth: function (pyEventName, pyHandler) {
                const eventName = this.$verifyEventName(pyEventName, "set event handler");
                this.$verifyCallable(eventName, pyHandler);
                const eventHandlers = (this._Component.eventHandlers[eventName] ||= []);
                eventHandlers.push(pyHandler);
                return Sk.builtin.none.none$;
            },
            $flags: { MinArgs: 2, MaxArgs: 2 },
            $doc: "Add an event handler function to be called when the event happens on this component. Event handlers will be called in the order they are added. Adding the same event handler multiple times will mean it gets called multiple times.",
        },
        /*!defBuiltinMethod(,event_name, [handler_func:callable])!2*/
        "remove_event_handler": {
            $meth: function (pyEventName, pyHandler) {
                const eventName = this.$verifyEventName(pyEventName, "remove event handler");
                if (pyHandler === undefined) {
                    // remove all the handlers
                    delete this._Component.eventHandlers[eventName];
                    return Sk.builtin.none.none$;
                }
                this.$verifyCallable(eventName, pyHandler);
                const currentHandlers = this._Component.eventHandlers[eventName];
                if (currentHandlers === undefined) {
                    throw new Sk.builtin.LookupError(`event handler '${pyHandler}' was not found in '${eventName}' event handlers for this component`);
                }
                const eventHandlers = currentHandlers.filter((handler) => handler !== pyHandler && Sk.misceval.richCompareBool(handler, pyHandler, "NotEq"));
                if (eventHandlers.length === currentHandlers.length) {
                    throw new Sk.builtin.LookupError(`event handler '${pyHandler}' was not found in '${eventName}' event handlers for this component`);
                }
                else if (eventHandlers.length) {
                    this._Component.eventHandlers[eventName] = eventHandlers;
                }
                else {
                    // we delete as a signal that the event handlers for this event don't exist. See link click event for example
                    delete this._Component.eventHandlers[eventName];
                }
                return Sk.builtin.none.none$;
            },
            $flags: { MinArgs: 1, MaxArgs: 2 },
            $doc: "Remove a specific event handler function for a given event. Calling remove_event_handler with just the event name will remove all the handlers for this event",
        },
        /*!defBuiltinMethod(,event_name,**event_args)!2*/
        "raise_event": {
            $meth: function (args, kws) {
                Sk.abstr.checkOneArg("raise_event", args);
                const eventName = this.$verifyEventName(args[0], "raise event");
                const listeners = this._Component.eventHandlers[eventName];
                if (!listeners) {
                    return Sk.builtin.none.none$;
                }
                const eventArgs = py.kwToObj(kws);
                eventArgs["sender"] = this;
                eventArgs["event_name"] = args[0];
                return Sk.misceval.chain(Sk.builtin.none.none$, ...listeners.map(pyFn => () => Sk.misceval.callsimOrSuspendArray(pyFn, [], py.objToKw(eventArgs))));
            },
            $flags: { FastCall: true },
            $doc: "Trigger the event on this component. Any keyword arguments are passed to the handler function.",
        },
        /*!defBuiltinMethod(_)!2*/
        "remove_from_parent": {
            $meth: function () {
                const parent = this._Component.parent;
                if (parent) {
                    delete this._Component.parent;
                    return parent.remove();
                }
                return Sk.builtin.none.none$;
            },
            $flags: { NoArgs: true },
            $doc: "Remove this component from its parent container.",
        },
        /*!defBuiltinMethod(,smooth=False)!2*/
        "scroll_into_view": {
            $meth: function (smooth) {
                const how = { behavior: Sk.misceval.isTrue(smooth) ? "smooth" : "instant", block: "center", inline: "center" };
                return Sk.misceval.chain(this.anvil$hooks.setupDom(), element => element.scrollIntoView(how));
            },
            $flags: { NamedArgs: ["smooth"], Defaults: [Sk.builtin.bool.false$] },
            $doc: "Scroll the window to make sure this component is in view.",
        },
        /*!defBuiltinMethod(tuple_of_event_handlers, event_name)!2*/
        "get_event_handlers": {
            $meth: function (eventName) {
                eventName = this.$verifyEventName(eventName, "get event handlers");
                return new Sk.builtin.tuple(this._Component.eventHandlers[eventName] || []);
            },
            $flags: { OneArg: true },
            $doc: "Get the current event_handlers for a given event_name",
        },
    },
    getsets: {
        parent: {
            $get() {
                return this._Component.parent?.pyParent || Sk.builtin.none.none$;
            },
            $set() {
                throw new Sk.builtin.AttributeError("Cannot set a '" + this.tp$name + "' component's parent this way - use 'add_component' on the container instead");
            },
        },
        __name__: {
            $get() {
                return Sk.abstr.lookupSpecial(this.ob$type, Sk.builtin.str.$name);
            },
        },
        tag: {
            $get() {
                return this._Component.tag;
            },
            $set(val) {
                this._Component.tag = val;
            },
            $doc: "Use this property to store any extra information about this component",
        },
        _anvil_hooks_: {
            $get() {
                return this._Component.pyHookWrapper;
            }
        }
    },
    flags: {
        sk$klass: true // tell skulpt we can be treated like a regular klass for tp$setatttr
    },
});
/*!defClass(anvil,Component)!*/
